// Guided setup, step 4: Documents (cycle 12).
//
// HR picks the forms this person needs from a searchable one-form-per-row checkbox list and assigns them together. Suggestions (a flag HR sets per form in the Form
// Library) only PRESELECT checkboxes: nothing is assigned until HR presses "Assign selected". Each assignment goes through the existing writer (assignHrDocument:
// duplicate protection, version pinning, HR-entered values) one at a time; the result of every form is shown as the SERVER answered it, and the assigned list is
// then re-read from the saved data. Assigning never signs, approves, reviews or sends anything to a provider.
//
// The "No documents needed" acknowledgment lives in the shell's footer beside Save & continue (offered only when the server says it is allowed) and is saved by that
// one action; the how-it-works help is rendered by the shell BELOW the footer.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  assignHrDocument, listHrDocumentAssignmentsForEmployee, listHrDocumentTemplates, listHrLiveSigningConfigs,
  type HrAssignmentPrompt, type HrAssignmentSummary, type HrDocumentAssignment, type HrDocumentTemplate,
} from '../../../api/client';
import { AssignValueInputs } from '../AssignValueInputs';
import { missingAssignmentValues } from '../formFieldsModel';
import { LoadViewNotice } from '../workspace/LoadNotices';
import { classifyLoadError, needsPermissionMessage, PERMISSION_LABEL, useSurfaceFlag, type LoadView } from '../workspace/loadState';
import { OUTLINE_BTN, PRIMARY_BTN, UnavailableNotice } from './stepUi';
import {
  equipmentTemplateIds, filterRows, formRows, listedAssignments, noDocumentsAck, pruneSelection, runAssignBatch, selectableIds, selectionAfterBatch,
  setVisibleSelected, statusChip, suggestedIds, toggleOne, valueNeeds, valuePayload, visibleSelectionState,
  type BatchItem, type FormRow, type ItemResult,
} from './documentsModel';
import { employeePath, setupPath } from './setupModel';
import type { StepContext } from './setupTypes';
import { useHrAccess } from '../HrAccessContext';
import { clearRetained, droppedNote, loadRetained, revalidateRetained, saveRetained, sweepRetained, type RetentionStorage } from './documentsRetention';

/** sessionStorage only (per tab, gone when the tab closes); null when the browser refuses it (the screen then simply remembers nothing). */
function tabStorage(): RetentionStorage | null {
  try { return typeof window !== 'undefined' ? window.sessionStorage : null; } catch { return null; }
}

const HELP_BODY = (
  <>
    <ol className="list-decimal pl-5 space-y-1">
      <li>Assign the forms this employee needs.</li>
      <li>The employee completes them using the available signing or upload option.</li>
      <li>HR reviews returned documents before marking them complete.</li>
    </ol>
    <p className="mt-3">You can continue setup while documents are waiting.</p>
  </>
);

const TONE: Record<'amber' | 'orange' | 'red' | 'blue', string> = {
  amber: 'bg-amber-50 text-amber-900 border-amber-300',
  orange: 'bg-orange-50 text-orange-900 border-orange-300',
  red: 'bg-red-50 text-red-800 border-red-300',
  blue: 'bg-blue-50 text-blue-800 border-blue-200',
};

function FormIcon({ className = 'w-5 h-5 text-gray-400' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={`${className} flex-shrink-0`} fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M7 3h7l4 4v14H7z" /><path d="M14 3v4h4" /><path d="M10 12h5M10 16h5" />
    </svg>
  );
}

/** Keyed by the person being set up, so nothing selected for one employee can ever carry over to another. */
export function DocumentsStep({ ctx }: { ctx: StepContext }) {
  return <DocumentsStepBody key={ctx.employeeId} ctx={ctx} />;
}

function DocumentsStepBody({ ctx }: { ctx: StepContext }) {
  const d = ctx.view.summary.documents;
  const savedNone = ctx.view.progress?.decisions.documents?.decision === 'not_applicable';
  const mayManage = useSurfaceFlag('mayManageDocuments');
  const cannotAssign = mayManage === false;
  const permissionText = needsPermissionMessage(PERMISSION_LABEL.documents, 'assign documents');
  const first = ctx.person.name && ctx.person.name !== 'This person' ? ctx.person.name.trim().split(/\s+/)[0] : null;

  // ── Saved data (each read is its own state: loading / ready / denied / unavailable is never drawn as "none") ───────────────────────
  const [templates, setTemplates] = useState<HrDocumentTemplate[]>([]);
  const [templatesView, setTemplatesView] = useState<LoadView>({ kind: 'loading' });
  const [assignments, setAssignments] = useState<HrDocumentAssignment[]>([]);
  const [assignmentsView, setAssignmentsView] = useState<LoadView>({ kind: 'loading' });
  const [prompts, setPrompts] = useState<Record<string, HrAssignmentPrompt[]>>({});
  const [summaries, setSummaries] = useState<Record<string, HrAssignmentSummary | undefined>>({});
  const [configsFailed, setConfigsFailed] = useState(false);
  const [listStale, setListStale] = useState(false);

  const loadTemplates = useCallback(() => {
    setTemplatesView((v) => (v.kind === 'ready' ? v : { kind: 'loading' }));
    listHrDocumentTemplates()
      .then((r) => { setTemplates(r.templates); setTemplatesView({ kind: 'ready' }); })
      .catch((e) => setTemplatesView((v) => (v.kind === 'ready' ? v : classifyLoadError(e, { gatedList: true }))));
  }, []);
  /** Re-reads this person's SAVED assignments. Returns whether the read succeeded (a failed re-read keeps what is shown and says so). */
  const loadAssignments = useCallback(async (): Promise<boolean> => {
    try {
      const r = await listHrDocumentAssignmentsForEmployee(ctx.employeeId);
      setAssignments(r.assignments); setAssignmentsView({ kind: 'ready' }); setListStale(false);
      return true;
    } catch (e) {
      setAssignmentsView((v) => (v.kind === 'ready' ? v : classifyLoadError(e, { targeted: true })));
      setListStale(true);
      return false;
    }
  }, [ctx.employeeId]);
  const loadConfigs = useCallback(() => {
    listHrLiveSigningConfigs()
      .then((r) => {
        setPrompts(Object.fromEntries(r.configs.map((c) => [c.templateVersionId, c.assignmentPrompts ?? []])));
        setSummaries(Object.fromEntries(r.configs.map((c) => [c.templateVersionId, c.assignmentSummary])));
        setConfigsFailed(false);
      })
      .catch(() => { setPrompts({}); setSummaries({}); setConfigsFailed(true); });
  }, []);
  useEffect(() => {
    setAssignmentsView({ kind: 'loading' });
    loadTemplates(); void loadAssignments(); loadConfigs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.employeeId]);

  const equipmentIds = useMemo(() => equipmentTemplateIds(templates), [templates]);
  const listed = useMemo(() => listedAssignments(assignments, equipmentIds), [assignments, equipmentIds]);
  const rows = useMemo(() => formRows(templates, assignments), [templates, assignments]);
  const ready = templatesView.kind === 'ready' && assignmentsView.kind === 'ready';

  // ── Selection ────────────────────────────────────────────────────────────────────────────────────────────────────────────────
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [touched, setTouched] = useState(false);
  const [search, setSearch] = useState('');
  const [panelOpen, setPanelOpen] = useState(false);
  const preselected = useRef(false);
  // Cycle 13: what HR had chosen before stepping away (Manage forms / a document's View) is remembered in this tab's sessionStorage, scoped to the signed-in caller AND
  // this employee, and re-checked against the freshly loaded lists before anything is shown (see documentsRetention.ts). It never assigns anything.
  const { state: access } = useHrAccess();
  const callerId = access.kind === 'ready' ? access.me.employeeId : null;
  const retentionScope = useMemo(() => ({ callerId, employeeId: ctx.employeeId }), [callerId, ctx.employeeId]);
  const retentionOn = Boolean(callerId) && mayManage === true; // unknown caller, unknown permission or no permission: nothing is stored or restored
  const [retentionReady, setRetentionReady] = useState(false); // false until the first restore decision has been applied: the (still empty) state must not overwrite what was remembered
  const [engaged, setEngaged] = useState(false); // HR has actually done something here (ticked/cleared, searched, assigned, or restored a choice): merely looking at the preselected suggestions is not remembered
  const [retentionNote, setRetentionNote] = useState<string | null>(null);
  // Housekeeping that does not wait for the lists: another caller's leftovers and expired entries are swept, and when the permission to assign is gone nothing remembered
  // is kept (even if the lists themselves are refused and so never load).
  useEffect(() => {
    const store = tabStorage();
    if (callerId) sweepRetained(store, callerId, Date.now());
    if (mayManage === false) clearRetained(store, retentionScope);
  }, [callerId, mayManage, retentionScope]);
  // Restore (or preselect) ONCE, when both reads have succeeded and the caller's permission is known. Nothing is assigned by this.
  useEffect(() => {
    if (!ready || preselected.current) return;
    preselected.current = true;
    const store = tabStorage();
    const kept = retentionOn ? loadRetained(store, retentionScope, Date.now()) : null;
    if (!kept) { setSelected(new Set(suggestedIds(rows))); setRetentionReady(true); return; } // nothing remembered: HR's own suggestions, as before
    const back = revalidateRetained(kept, selectableIds(rows), true);
    setSelected(back.selected); setSearch(back.search);
    setPanelOpen(back.panelOpen || back.selected.size > 0); // restored choices are always on screen, never hidden behind a closed list
    if (back.selected.size > 0) setTouched(true);
    setEngaged(true);
    setRetentionNote(droppedNote(back.dropped));
    setRetentionReady(true);
  }, [ready, rows, callerId, mayManage, retentionOn, retentionScope]);
  // Keep the remembered copy in step with what is on screen (an empty selection with no search text removes it).
  useEffect(() => {
    if (!retentionReady || !retentionOn || !engaged) return;
    saveRetained(tabStorage(), retentionScope, { selected: [...selected], search, panelOpen }, Date.now());
  }, [retentionReady, engaged, selected, search, panelOpen, retentionOn, retentionScope]);
  // A form that became unassignable (assigned meanwhile, retired) leaves the selection.
  useEffect(() => { if (ready) setSelected((s) => { const p = pruneSelection(s, rows); return p.size === s.size ? s : p; }); }, [ready, rows]);

  const suggestedCount = useMemo(() => suggestedIds(rows).length, [rows]);
  const visible = useMemo(() => filterRows(rows, search), [rows, search]);
  const hiddenSelected = useMemo(() => { const v = new Set(visible.map((r) => r.templateId)); return [...selected].filter((id) => !v.has(id)).length; }, [selected, visible]);
  const selState = visibleSelectionState(selected, visible);
  const selectAllRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => { if (selectAllRef.current) selectAllRef.current.indeterminate = selState === 'some'; }, [selState]);

  // ── HR-entered values (the existing "prompts" flow), per selected form ───────────────────────────────────────────────────────────
  const [values, setValues] = useState<Record<string, Record<string, string>>>({});
  const needs = useMemo(() => valueNeeds(selected, rows, prompts, values, missingAssignmentValues), [selected, rows, prompts, values]);
  const stillMissing = needs.filter((n) => n.missing.length > 0);

  // ── Assigning ────────────────────────────────────────────────────────────────────────────────────────────────────────────────
  const busyRef = useRef(false);
  const [busy, setBusy] = useState(false);
  const [inFlight, setInFlight] = useState<string | null>(null);
  const [queued, setQueued] = useState<Set<string>>(new Set());
  const [results, setResults] = useState<Record<string, ItemResult>>({});
  const doneRef = useRef<Set<string>>(new Set()); // forms that succeeded in this visit: never sent again
  const [outcome, setOutcome] = useState<{ assigned: number; already: number; failed: number; refreshed: boolean } | null>(null);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  async function assignSelected() {
    if (busyRef.current || cannotAssign || !ready) return;
    const chosen = rows.filter((r) => selected.has(r.templateId) && !r.assigned && !doneRef.current.has(r.templateId));
    if (chosen.length === 0 || stillMissing.length > 0) return;
    busyRef.current = true; setBusy(true); setOutcome(null); setEngaged(true);
    setPanelOpen(true); // the per-form results live in the list: keep it on screen while (and after) the batch runs, even once the first assignment makes the saved list non-empty
    setQueued(new Set(chosen.map((c) => c.templateId)));
    setResults((prev) => { const next = { ...prev }; for (const c of chosen) delete next[c.templateId]; return next; });
    const items: BatchItem[] = chosen.map((c) => ({ templateId: c.templateId, versionId: c.versionId, name: c.name, fieldValues: valuePayload(prompts[c.versionId] ?? [], values[c.templateId] ?? {}) }));
    const batch = await runAssignBatch(
      items,
      doneRef.current,
      async (item) => {
        if (alive.current) setInFlight(item.templateId);
        await assignHrDocument(ctx.employeeId, item.templateId, item.versionId, item.fieldValues);
      },
      (id, r) => {
        if (r.state === 'assigned' || r.state === 'already') doneRef.current.add(id);
        if (!alive.current) return;
        setResults((prev) => ({ ...prev, [id]: r }));
        setQueued((q) => { const n = new Set(q); n.delete(id); return n; });
      },
      permissionText,
    );
    // The truth is the SAVED list: re-read it (and the setup summary) before anything is called done.
    const refreshed = await loadAssignments();
    await ctx.reload();
    if (!alive.current) return;
    const vals = Object.values(batch);
    setOutcome({ assigned: vals.filter((v) => v.state === 'assigned').length, already: vals.filter((v) => v.state === 'already').length, failed: vals.filter((v) => v.state === 'failed').length, refreshed });
    setSelected((s) => selectionAfterBatch(s, batch));
    setRetentionNote(null);
    // Everything went through: nothing is left to remember (the failed ones, if any, stay selected and stay remembered for the retry).
    if (vals.length > 0 && !vals.some((v) => v.state === 'failed')) { clearRetained(tabStorage(), retentionScope); setSearch(''); }
    setInFlight(null); setQueued(new Set()); setTouched(false);
    busyRef.current = false; setBusy(false);
    if (vals.some((v) => v.state === 'assigned' || v.state === 'already') && !vals.some((v) => v.state === 'failed')) setPanelOpen(false);
  }

  // ── Footer: "No documents needed" acknowledgment + help (the shell renders both) ────────────────────────────────────────────────
  const [ackChecked, setAckChecked] = useState(savedNone);
  const ack = noDocumentsAck(ctx.view.summary.noneNeeded?.documents?.allowed, assignmentsView.kind === 'ready', d.state === 'available' ? d.liveCount : 0);
  const ackRef = useRef(ackChecked); ackRef.current = ackChecked;
  const ackShowRef = useRef(ack.show); ackShowRef.current = ack.show;
  const savedRef = useRef(savedNone); savedRef.current = savedNone;
  const busyStateRef = useRef(busy); busyStateRef.current = busy;
  const ctxRef = useRef(ctx); ctxRef.current = ctx;
  const available = d.state === 'available';

  useEffect(() => {
    if (!available) { ctx.bindSaver(null); ctx.setDirty(false); return; }
    ctx.bindSaver(async () => {
      if (busyRef.current) return { ok: false as const, message: 'Documents are still being assigned. Wait for them to finish, then continue.' };
      if (ackRef.current && ackShowRef.current) return { ok: true as const, decisions: { documents: { decision: 'not_applicable' as const } } };
      // Unticked, or no longer allowed because documents now exist: clear the earlier acknowledgment (no record is touched).
      if (savedRef.current && (!ackRef.current || !ackShowRef.current)) return { ok: true as const, decisions: { documents: null } };
      return { ok: true as const };
    });
    return () => ctx.bindSaver(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [available]);
  useEffect(() => { ctxRef.current.setDirty(touched && selected.size > 0); }, [touched, selected]);

  useEffect(() => {
    ctxRef.current.bindFooter({
      ack: available && ack.show ? { id: 'documents-none-needed', label: 'No documents needed', checked: ackChecked, onChange: setAckChecked, hint: ack.reason ?? undefined } : undefined,
      help: { title: 'How document setup works', body: HELP_BODY },
      invalidMessage: busy ? 'Documents are still being assigned. Wait for them to finish, then continue.' : null,
    });
  }, [available, ack.show, ack.reason, ackChecked, busy]);
  useEffect(() => () => ctxRef.current.bindFooter(null), []);

  if (d.state === 'unavailable') {
    return (
      <section className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 sm:p-6" data-testid="step-documents">
        <h2 className="text-base font-semibold text-gray-900">Documents</h2>
        <p className="text-xs text-gray-500 mt-0.5 mb-4">Assign the forms {first ?? 'this person'} needs to complete.</p>
        <UnavailableNotice title="Documents" reason={d.reason} capability={d.capability} />
      </section>
    );
  }

  const noneListed = assignmentsView.kind === 'ready' && listed.length === 0;
  const showPanel = panelOpen || noneListed;
  const nSelected = selected.size;
  const subtitle = noneListed && suggestedCount > 0 ? 'Review the suggested forms, then assign them together.' : `Assign the forms ${first ?? 'this person'} needs to complete.`;
  const manageState = { returnTo: setupPath(ctx.employeeId, 'documents'), employeeName: ctx.person.name === 'This person' ? undefined : ctx.person.name };

  return (
    <div className="space-y-4" data-testid="step-documents">
      <section className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 sm:p-6" aria-labelledby="documents-heading">
        <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
          <div className="min-w-0">
            <h2 id="documents-heading" className="text-base font-semibold text-gray-900">Documents</h2>
            <p className="text-sm text-gray-600 mt-0.5" data-testid="documents-subtitle">{subtitle}</p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-3">
            {mayManage !== false && (
              <Link to="/hr/admin/documents" state={manageState} className="text-sm font-medium text-[#176A9B] hover:underline" data-testid="documents-manage-forms">Manage forms</Link>
            )}
            {!noneListed && assignmentsView.kind === 'ready' && (
              <button type="button" className={PRIMARY_BTN} onClick={() => setPanelOpen((v) => !v)} aria-expanded={showPanel} aria-controls="documents-select-panel" data-testid="documents-open-panel">
                {showPanel ? 'Hide form list' : 'Assign documents'}
              </button>
            )}
          </div>
        </div>

        {cannotAssign && <p className="mt-3 text-xs text-amber-800" data-testid="documents-assign-needs-permission">{permissionText}</p>}
        {(templatesView.kind === 'denied' || templatesView.kind === 'unavailable') && (
          <div className="mt-3"><LoadViewNotice view={templatesView} thing="the forms list" permission={PERMISSION_LABEL.documents} onRetry={loadTemplates} testIdPrefix="documents-templates" /></div>
        )}
        {(assignmentsView.kind === 'denied' || assignmentsView.kind === 'targetUnavailable' || assignmentsView.kind === 'unavailable') && (
          <div className="mt-3"><LoadViewNotice view={assignmentsView} thing="this person’s documents" permission={PERMISSION_LABEL.documents} onRetry={() => { setAssignmentsView({ kind: 'loading' }); void loadAssignments(); }} testIdPrefix="documents-assignments" /></div>
        )}
        {(templatesView.kind === 'loading' || assignmentsView.kind === 'loading') && <p className="mt-4 text-sm text-gray-500" role="status" data-testid="documents-loading">Loading documents…</p>}

        {showPanel && ready && (
          <div id="documents-select-panel" className="mt-4" data-testid="documents-select-panel">
            {rows.length === 0 ? (
              <p className="text-sm text-gray-600 rounded-md border border-gray-200 bg-gray-50 px-4 py-3" data-testid="documents-no-forms">
                No forms are available to assign yet.{mayManage !== false ? ' Add forms in Manage forms.' : ''}
              </p>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  {suggestedCount > 0 && (
                    <span className="text-xs font-medium text-[#176A9B] bg-[#EDF5FC] border border-[#B9DAF0] rounded-full px-2.5 py-1" data-testid="documents-suggested-chip">{suggestedCount} suggested from company setup</span>
                  )}
                  <input
                    type="search" value={search} onChange={(e) => { setEngaged(true); setSearch(e.target.value); }}
                    placeholder="Search forms" aria-label="Search forms" autoComplete="off"
                    className="flex-1 min-w-[12rem] border border-gray-300 rounded-md px-3 py-1.5 text-sm" data-testid="documents-search"
                  />
                </div>

                {retentionNote && <p className="mb-3 text-xs text-gray-700 rounded-md border border-gray-200 bg-gray-50 px-3 py-2" role="status" data-testid="documents-retention-note">{retentionNote}</p>}
                <div className="rounded-md border border-gray-200 overflow-hidden">
                  <div className="flex items-center justify-between gap-3 bg-gray-50 border-b border-gray-200 px-3 py-2">
                    <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-800 cursor-pointer">
                      <input
                        ref={selectAllRef} type="checkbox" className="h-4 w-4 accent-[#2F7FB8]"
                        checked={selState === 'all'} disabled={busy || selectableIds(visible).length === 0}
                        onChange={(e) => { setTouched(true); setEngaged(true); setRetentionNote(null); setSelected((s) => setVisibleSelected(s, visible, e.target.checked)); }}
                        aria-label={search.trim() ? 'Select all forms matching the search' : 'Select all available forms'} data-testid="documents-select-all"
                      />
                      <span>Available forms</span>
                    </label>
                    <span className="text-sm text-gray-600" aria-live="polite" data-testid="documents-selected-count">
                      {nSelected} selected{hiddenSelected > 0 ? ` (${hiddenSelected} hidden by search)` : ''}
                    </span>
                  </div>
                  {/* About 7 rows show at once, then the list scrolls inside its own box (focusable so the keyboard can scroll it). */}
                  <ul className="max-h-72 overflow-y-auto divide-y divide-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#4AABDE]" tabIndex={0} aria-label="Available forms" data-testid="documents-form-list">
                    {visible.length === 0 && <li className="px-3 py-3 text-sm text-gray-500" data-testid="documents-no-match">No forms match “{search.trim()}”.</li>}
                    {visible.map((r) => (
                      <FormListRow
                        key={r.templateId} row={r} checked={selected.has(r.templateId)} disabled={busy || r.assigned || doneRef.current.has(r.templateId)}
                        result={results[r.templateId]} inFlight={inFlight === r.templateId} queued={queued.has(r.templateId)}
                        onToggle={() => { setTouched(true); setEngaged(true); setRetentionNote(null); setSelected((s) => toggleOne(s, r)); }}
                      />
                    ))}
                  </ul>
                </div>

                {configsFailed && (
                  <p className="mt-3 text-xs text-amber-800" role="status" data-testid="documents-configs-failed">
                    The signing setup details could not be loaded. A form that needs information from HR will be refused by the server and shown as not assigned.
                  </p>
                )}

                {needs.length > 0 && (
                  <div className="mt-4 space-y-3" data-testid="documents-values">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Information HR enters for the selected forms</h3>
                    {needs.map((n) => (
                      <div key={n.row.templateId} className="rounded-md border border-gray-200 bg-white p-3" data-testid={`documents-values-${n.row.templateId}`}>
                        <div className="text-sm font-medium text-gray-900 mb-1">{n.row.name}</div>
                        <AssignValueInputs
                          prompts={n.prompts} summary={summaries[n.row.versionId] ?? null} idPrefix={`docs-${n.row.templateId}`}
                          values={values[n.row.templateId] ?? {}}
                          onChange={(next) => setValues((v) => ({ ...v, [n.row.templateId]: next }))}
                        />
                        {n.missing.length > 0 && <p className="mt-1 text-xs text-amber-800">Still needed: {n.missing.join(', ')}.</p>}
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
                  <p className="text-xs text-gray-600 min-w-0" data-testid="documents-confirm-note">
                    {suggestedCount > 0 ? 'Suggested selections are not assigned until you confirm.' : 'Nothing is assigned until you confirm.'}
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button" className={PRIMARY_BTN} onClick={assignSelected}
                      disabled={busy || cannotAssign || nSelected === 0 || stillMissing.length > 0}
                      title={cannotAssign ? permissionText : stillMissing.length > 0 ? 'Enter the information the selected forms need first.' : undefined}
                      data-testid="documents-assign-selected"
                    >
                      {busy ? 'Assigning…' : `Assign selected (${nSelected})`}
                    </button>
                    <button type="button" className={OUTLINE_BTN} onClick={() => { setTouched(true); setEngaged(true); setRetentionNote(null); setSelected(new Set()); clearRetained(tabStorage(), retentionScope); }} disabled={busy || nSelected === 0} data-testid="documents-clear-selection">Clear selection</button>
                  </div>
                </div>
                {stillMissing.length > 0 && <p className="mt-2 text-xs text-amber-800" role="status" data-testid="documents-values-missing">Enter the information shown above for {stillMissing.length === 1 ? 'one selected form' : `${stillMissing.length} selected forms`} before assigning.</p>}
              </>
            )}

          </div>
        )}

        {/* What the server answered for the last batch. Outside the list so it stays visible after a fully successful batch closes the list. */}
        {outcome && (
          <div
            role={outcome.failed > 0 ? 'alert' : 'status'}
            className={`mt-4 rounded-md border px-4 py-3 text-sm ${outcome.failed > 0 ? 'border-red-200 bg-red-50 text-red-900' : 'border-green-200 bg-green-50 text-green-900'}`}
            data-testid="documents-outcome"
          >
            <div className="font-medium">
              {outcome.assigned > 0 && `Assigned ${outcome.assigned} form${outcome.assigned === 1 ? '' : 's'}. `}
              {outcome.already > 0 && `${outcome.already} ${outcome.already === 1 ? 'was' : 'were'} already assigned. `}
              {outcome.failed > 0 && `${outcome.failed} could not be assigned.`}
            </div>
            {outcome.failed > 0 && <p className="text-xs mt-0.5">The forms that failed are still selected so you can try again. Forms already assigned will not be assigned twice.</p>}
            {!outcome.refreshed && <p className="text-xs mt-0.5">The saved list could not be re-read just now, so the list below may be out of date.</p>}
          </div>
        )}
      </section>

      <section className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 sm:p-6" aria-labelledby="documents-assigned-heading" data-testid="documents-assigned">
        <h2 id="documents-assigned-heading" className="text-sm font-semibold text-gray-900">
          Assigned documents{assignmentsView.kind === 'ready' ? ` (${listed.length})` : ''}
        </h2>
        {listStale && assignmentsView.kind === 'ready' && (
          <p className="mt-2 text-xs text-amber-800" role="alert" data-testid="documents-list-stale">
            This list could not be refreshed and may be out of date. <button type="button" className="underline" onClick={() => void loadAssignments()}>Try again</button>
          </p>
        )}
        {assignmentsView.kind === 'loading' && <p className="mt-3 text-sm text-gray-500">Loading…</p>}
        {assignmentsView.kind !== 'loading' && assignmentsView.kind !== 'ready' && <p className="mt-3 text-sm text-gray-600">The assigned documents can’t be shown right now. This is not the same as none being assigned.</p>}
        {noneListed && <p className="mt-3 text-sm text-gray-500" data-testid="documents-none-assigned">No documents assigned yet.</p>}
        {listed.length > 0 && (
          <ul className="mt-3 divide-y divide-gray-100" data-testid="documents-assigned-list">
            {listed.map((a) => {
              const chip = statusChip(a.status);
              return (
                <li key={a.assignmentId} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5" data-testid="documents-assigned-row">
                  <FormIcon />
                  <span className="min-w-0 flex-1 text-sm font-medium text-gray-900 truncate" title={a.templateName}>{a.templateName} <span className="text-xs font-normal text-gray-400">v{a.versionNumber}</span></span>
                  {chip && <span className={`text-xs font-medium border rounded-full px-2.5 py-0.5 ${TONE[chip.tone]}`} data-testid="documents-status-chip">{chip.label}</span>}
                  <Link
                    to={`${employeePath(ctx.employeeId)}?section=documents&item=${encodeURIComponent(a.assignmentId)}`}
                    state={manageState}
                    className="text-sm font-medium text-[#176A9B] hover:underline" aria-label={`View ${a.templateName}`} data-testid="documents-view"
                  >View →</Link>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}

function FormListRow({ row, checked, disabled, result, inFlight, queued, onToggle }: {
  row: FormRow; checked: boolean; disabled: boolean; result: ItemResult | undefined; inFlight: boolean; queued: boolean; onToggle: () => void;
}) {
  const id = `docs-form-${row.templateId}`;
  const done = result?.state === 'assigned' ? 'Assigned' : result?.state === 'already' || row.assigned ? 'Already assigned' : null;
  return (
    <li className="px-3 py-2 min-h-[2.5rem]" data-testid="documents-form-row" data-template-id={row.templateId}>
      <div className="flex items-center gap-3">
        <input id={id} type="checkbox" className="h-4 w-4 flex-shrink-0 accent-[#2F7FB8]" checked={checked && !done} disabled={disabled} onChange={onToggle} aria-describedby={`${id}-state`} />
        <label htmlFor={id} className={`flex-1 min-w-0 flex items-center gap-2 text-sm ${done ? 'text-gray-500' : 'text-gray-900 cursor-pointer'}`}>
          <FormIcon className="w-4 h-4 text-gray-400" />
          <span className="truncate" title={row.name}>{row.name}</span>
          <span className="text-xs text-gray-400 flex-shrink-0">v{row.versionNumber}</span>
          {row.suggested && !done && <span className="text-[10px] font-medium text-[#176A9B] bg-[#EDF5FC] border border-[#B9DAF0] rounded px-1.5 py-0.5 flex-shrink-0">Suggested</span>}
        </label>
        <span id={`${id}-state`} className="text-xs flex-shrink-0" data-testid="documents-row-state">
          {inFlight ? <span className="text-gray-600">Assigning…</span>
            : queued ? <span className="text-gray-500">Waiting…</span>
            : done ? <span className="text-green-800 font-medium">{done === 'Assigned' ? '✓ Assigned' : 'Already assigned'}</span>
            : result?.state === 'failed' ? <span className="text-red-700 font-medium">Not assigned</span>
            : row.earlierVersionAssigned ? <span className="text-gray-500">An earlier version is assigned</span>
            : null}
        </span>
      </div>
      {result?.state === 'failed' && <p className="mt-1 ml-7 text-xs text-red-700" role="alert" data-testid="documents-row-error">{result.message}</p>}
    </li>
  );
}
